import { resolveClientDataActualFields } from '@/common/utils/resolveClientDataActualFields'
import { preserveUploadedFileAttachmentsFields } from '@/common/utils/fileAttachmentsUniData'
import type { ActionContext, ActionDependencies, ExchangeData } from '@/services/action/types.ts'
import { createSafeClientData } from '@/services/core/core.service.ts'

export function getActionLayoutMeta(
  context: Pick<ActionContext, 'level' | 'data' | 'elementId'>,
): { level: string; position: string; layoutId: string } {
  const layout = context.data?.layout || {}
  return {
    level: String(layout.level?._text || context.level || '').toLowerCase(),
    position: String(layout.position?._text || '').toLowerCase(),
    layoutId: String(layout.id?._text ?? layout.id ?? context.elementId ?? '').trim(),
  }
}

/** Action привязан к конкретному element layout (не panel/form/page). */
export function isActionDirectlyOnElement(
  context: Pick<ActionContext, 'level' | 'data' | 'elementId'>,
  targetElementId: string | number | undefined,
): boolean {
  const { level, layoutId } = getActionLayoutMeta(context)
  if (level !== 'element') return false
  const targetId = String(targetElementId ?? '').trim()
  return targetId !== '' && layoutId === targetId
}

/**
 * Checkbox-selection для таблицы в payload action:
 * - position=top -> учитывать чекбоксы;
 * - action не на этой таблице (panel/form/другой element) -> учитывать;
 * - action на таблице и position!=top -> полная таблица.
 */
export function shouldApplyCheckboxTableSelectionForTableField(
  context: Pick<ActionContext, 'level' | 'data' | 'elementId'>,
  tableElementId: string | number | undefined,
): boolean {
  const { position } = getActionLayoutMeta(context)
  if (position === 'top') return true
  return !isActionDirectlyOnElement(context, tableElementId)
}

export function resolveTableElementIdForFieldName(
  fieldName: string,
  context: Pick<ActionContext, 'formId'>,
  dependencies?: ActionDependencies,
): string | undefined {
  const trimmed = String(fieldName ?? '').trim()
  if (!trimmed) return undefined

  const elements = dependencies?.formProvider?.getElements?.()
  if (!elements || typeof elements !== 'object') return undefined

  const contextFormId = String(context.formId ?? '').trim()

  for (const [name, element] of Object.entries(elements)) {
    const reg = element as {
      fieldName?: unknown
      elementId?: unknown
      formId?: unknown
      ctx?: { id?: unknown; formId?: unknown }
    }
    const regFieldName = String(reg.fieldName ?? name).trim()
    if (regFieldName !== trimmed && name !== trimmed) continue

    const regFormId = String(reg.formId ?? reg.ctx?.formId ?? '').trim()
    if (contextFormId && regFormId && regFormId !== contextFormId) continue

    const id = reg.elementId ?? reg.ctx?.id
    if (id == null || id === '') continue
    return String(id)
  }

  return undefined
}

function toActionContextLevel(value: unknown): ActionContext['level'] {
  const level = String(value ?? '').toLowerCase()
  if (level === 'form' || level === 'panel' || level === 'element' || level === 'page') {
    return level
  }
  return 'element'
}

export function shouldCheckboxTableExportAllRowsForAction(
  action: unknown,
  tableElementId: string | number | null | undefined,
): boolean {
  if (!action || typeof action !== 'object') return false
  const ctx = action as Record<string, unknown>
  const pseudoContext: Pick<ActionContext, 'level' | 'data' | 'elementId'> = {
    level: toActionContextLevel(ctx.level),
    elementId: ctx.elementId as ActionContext['elementId'],
    data: ctx.data as ActionContext['data'],
  }
  return !shouldApplyCheckboxTableSelectionForTableField(pseudoContext, tableElementId ?? undefined)
}

/**
 * Имена checkbox-таблиц для overlay после onClick.
 * Пустой selection (`rows: []`) и отсутствие записи в Map всё равно входят:
 * recreate dump не должен подменить пустую таблицу полным store.
 */
export function collectCheckboxSelectionPayloadFieldNames(
  metas: ReadonlyArray<{ fieldName?: unknown; withCheckBoxes?: boolean }>,
  selections?: ReadonlyArray<{ fieldName?: unknown }> | null,
): string[] {
  const names = new Set<string>()
  for (const meta of metas) {
    const fieldName = String(meta?.fieldName ?? '').trim()
    if (!fieldName || !meta.withCheckBoxes) continue
    names.add(fieldName)
  }
  for (const sel of selections ?? []) {
    const fieldName = String(sel?.fieldName ?? '').trim()
    if (!fieldName) continue
    names.add(fieldName)
  }
  return Array.from(names)
}

/** После onClick mergeFieldMaps может вернуть полную таблицу вместо checkbox-selection. */
export function overlaySelectionPayloadFieldsOnExchangeData(
  target: ExchangeData,
  source: ExchangeData | undefined,
  fieldNames: readonly string[] | undefined,
): ExchangeData {
  if (!source || !fieldNames?.length) {
    return target
  }

  const sourceFields = resolveClientDataActualFields(source)
  const existingFields = resolveClientDataActualFields(target)
  const merged = { ...existingFields }

  for (const rawName of fieldNames) {
    const fieldName = String(rawName ?? '').trim()
    if (!fieldName || sourceFields[fieldName] === undefined) continue
    merged[fieldName] = sourceFields[fieldName]
  }

  return createSafeClientData({
    ...(target || {}),
    fields: merged,
    clientData: target?.clientData || {},
  })
}

const ONCLICK_DOCUMENT_IDENTITY_KEYS = ['_id', '_typeId', '_versionId'] as const

/**
 * Recreate layout-среза после onClick (tiles PK overlay) не должен затирать
 * `_id` / `_typeId` / `_versionId`, которые скрипт уже выставил.
 */
export function preserveOnClickDocumentIdentity(
  target: ExchangeData,
  onClickSnapshot: ExchangeData | undefined,
): ExchangeData {
  if (!onClickSnapshot) {
    return target
  }

  const onClickFields = resolveClientDataActualFields(onClickSnapshot)
  const existingFields = resolveClientDataActualFields(target)
  const merged = { ...existingFields }
  let hasOverride = false

  for (const key of ONCLICK_DOCUMENT_IDENTITY_KEYS) {
    const fromOnClick = onClickFields[key]
    if (fromOnClick === undefined) continue
    merged[key] = fromOnClick
    hasOverride = true
  }

  if (!hasOverride) {
    return target
  }

  return createSafeClientData({
    ...(target || {}),
    fields: merged,
    clientData: target?.clientData || {},
  })
}

/**
 * Таблицы, собранные action без checkbox-selection (putData/putFullData в onClick), не затирать merge-ом.
 */
export function preserveOnClickTableFieldsNotInSelectionPayload(
  target: ExchangeData,
  onClickSnapshot: ExchangeData | undefined,
  selectionPayloadFieldNames: readonly string[],
): ExchangeData {
  if (!onClickSnapshot) {
    return target
  }

  const skip = new Set(selectionPayloadFieldNames.map((name) => String(name ?? '').trim()).filter(Boolean))
  const onClickFields = resolveClientDataActualFields(onClickSnapshot)
  const existingFields = resolveClientDataActualFields(target)
  const merged = { ...existingFields }
  let hasOverride = false

  for (const [fieldName, fieldData] of Object.entries(onClickFields)) {
    if (skip.has(fieldName)) continue
    const isTable =
      fieldData != null &&
      typeof fieldData === 'object' &&
      (fieldData as { type?: string }).type === 'UniDataTable'
    if (isTable) {
      merged[fieldName] = fieldData
      hasOverride = true
      continue
    }
    if (existingFields[fieldName] === undefined) {
      merged[fieldName] = fieldData
      hasOverride = true
    }
  }

  if (!hasOverride) {
    return target
  }

  return createSafeClientData({
    ...(target || {}),
    fields: merged,
    clientData: target?.clientData || {},
  })
}

/**
 * FILEATTACHMENTS из putFormData/getFullData с uploaded `code` не затирать store dump (`code`≤0).
 */
export function preserveUploadedFileAttachmentsOnExchangeData(
  target: ExchangeData,
  onClickSnapshot: ExchangeData | undefined,
  fieldNames: Iterable<string>,
): ExchangeData {
  if (!onClickSnapshot) {
    return target
  }
  const picked = preserveUploadedFileAttachmentsFields(
    resolveClientDataActualFields(target),
    resolveClientDataActualFields(onClickSnapshot),
    fieldNames,
  )
  if (!picked.changed) {
    return target
  }
  return createSafeClientData({
    ...(target || {}),
    fields: picked.fields,
    clientData: target?.clientData || {},
  })
}

export function omitUnselectedUniDataTablesFromExchangeData(
  data: ExchangeData,
  keepTableFieldNames: readonly string[] = [],
): ExchangeData {
  const keep = new Set(
    keepTableFieldNames.map((name) => String(name ?? '').trim()).filter(Boolean),
  )
  const fields = resolveClientDataActualFields(data)
  if (!fields || typeof fields !== 'object') return data

  const next = { ...fields }
  let changed = false
  for (const [key, value] of Object.entries(fields)) {
    if (keep.has(key)) continue
    if (
      value != null &&
      typeof value === 'object' &&
      (value as { type?: string }).type === 'UniDataTable'
    ) {
      delete next[key]
      changed = true
    }
  }
  if (!changed) return data

  return createSafeClientData({
    ...(data || {}),
    fields: next,
    clientData: data?.clientData || {},
  })
}
